import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { SEOHead } from '@/components/SEOHead';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Search, Building2, Home, MapPin, TrendingUp, Users, Shield, ArrowRight,
  Landmark, Calculator, BarChart3, Newspaper,
  ChevronLeft, ChevronRight, Star, Phone, ExternalLink, ShieldCheck, Handshake, Lock, SlidersHorizontal, RotateCcw, Filter,
  LucideIcon, LandPlot, Briefcase, Image as ImageIcon, FileText, Award, Map,
} from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import heroBg from '@/assets/hero-bg.jpg';
import homeAdvisorAiIcon from '@/assets/home-advisor-ai.png';
import avmIcon from '@/assets/avm.png';
import newProjectsIcon from '@/assets/new-projects.png';
import societyMapsIcon from '@/assets/society-maps.png';
import areaGuidesIcon from '@/assets/area-guides.png';
import costCalculatorIcon from '@/assets/cost-calculator.png';
import ethioPropertyInvestIcon from '@/assets/ethio-property-invest.png';
import { optimizedImageUrl, optimizedSrcSet } from '@/lib/imageOptimizer';
import {
  useHero, useCities, useExploreTiles, useLatestNews, useLatestBlog, useLocalize,
} from '@/hooks/useLandingContent';
import { useCurrency } from '@/contexts/CurrencyContext';
import { formatPrice, formatPriceRange } from '@/lib/formatPrice';
import { CurrencySwitcher } from '@/components/CurrencySwitcher';
import { FeedStoriesRow } from '@/components/property/FeedStoriesRow';
import { FavoritePropertiesRow } from '@/components/landing/FavoritePropertiesRow';
import { ViewedProjectsRow } from '@/components/landing/ViewedProjectsRow';
import { ViewedPropertiesRow } from '@/components/landing/ViewedPropertiesRow';
import { TitaniumAgencies } from '@/components/landing/TitaniumAgencies';
import { TrendingProjectsTwoRow } from '@/components/landing/TrendingProjectsTwoRow';
import { CommunitySection } from '@/components/landing/CommunitySection';

import { PropertyScrollRow } from '@/components/landing/PropertyScrollRow';
import { PropertyCard } from '@/components/property/PropertyCard';
import { RecommendedPropertyCard } from '@/components/property/RecommendedPropertyCard';
import { NewPropertiesRow } from '@/components/landing/NewPropertiesRow';
import { MobileBrowsePropertiesCard } from '@/components/landing/MobileBrowsePropertiesCard';
import { HeroPropertyTypePopover, HeroCategory } from '@/components/landing/HeroPropertyTypePopover';
import { MobileNewsScroll } from '@/components/landing/MobileNewsScroll';
import { MobilePostAdCTA } from '@/components/landing/MobilePostAdCTA';
import { MobileAgenciesRow } from '@/components/landing/MobileAgenciesRow';
import { MobileTitaniumAgencies } from '@/components/landing/MobileTitaniumAgencies';
import { useIsMobile } from '@/hooks/use-mobile';
import { BannerSlot } from '@/components/banners/BannerSlot';

// Map icon names stored in `explore_tiles.icon_name` to Lucide components
const ICON_MAP: Record<string, LucideIcon> = {
  Landmark, Calculator, BarChart3, MapPin, Map, Building2, Home, TrendingUp, Users,
  Shield, LandPlot, Briefcase, FileText, Award, Newspaper, Image: ImageIcon,
};

export default function Index() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const localize = useLocalize();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'buy' | 'rent' | 'projects'>('buy');
  const [selectedCity, setSelectedCity] = useState('Addis Ababa');
  const [heroCategory, setHeroCategory] = useState<HeroCategory>('homes');
  const [heroSubtype, setHeroSubtype] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [minArea, setMinArea] = useState('');
  const [maxArea, setMaxArea] = useState('');
  const [beds, setBeds] = useState('all');
  const agencyScrollRef = useRef<HTMLDivElement>(null);
  const projectScrollRef = useRef<HTMLDivElement>(null);
  const { displayCurrency, rate } = useCurrency();
  const isMobile = useIsMobile();

  // CMS-driven content
  const { data: hero } = useHero();
  const { data: citiesData } = useCities();
  const { data: exploreTiles } = useExploreTiles();
  const { data: latestNews } = useLatestNews(3);
  const { data: blogPostsData } = useLatestBlog(3);

  const cityNames = (citiesData && citiesData.length > 0)
    ? citiesData.map(c => c.name)
    : ['Addis Ababa'];

  // Keep selected city valid as the list loads
  useEffect(() => {
    if (cityNames.length > 0 && !cityNames.includes(selectedCity)) {
      setSelectedCity(cityNames[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [citiesData]);


  // Dynamic data queries
  const { data: approvedProjects } = useQuery({
    queryKey: ['landing-projects'],
    queryFn: async () => {
      const { data } = await supabase
        .from('projects')
        .select('*, project_images(url, is_primary), developer_profiles(company_name, phone, whatsapp)')
        .eq('approval_status', 'approved')
        .order('created_at', { ascending: false })
        .limit(8);
      return data || [];
    },
  });

  const { data: verifiedAgencies } = useQuery({
    queryKey: ['landing-agencies'],
    queryFn: async () => {
      const { data } = await supabase
        .from('agencies')
        .select('*')
        .eq('is_verified', true)
        .limit(10);
      return data || [];
    },
  });

  const { data: newProperties } = useQuery({
    queryKey: ['landing-new-properties'],
    queryFn: async () => {
      // Include both freshly created listings AND boosted/refreshed ones
      // (featured first, then most recently updated).
      const { data } = await supabase
        .from('properties')
        .select('*, property_images(url, is_primary)')
        .eq('approval_status', 'approved')
        .eq('status', 'active')
        .order('is_featured', { ascending: false })
        .order('updated_at', { ascending: false })
        .limit(12);
      return data || [];
    },
  });

  const { data: stats } = useQuery({
    queryKey: ['landing-stats'],
    queryFn: async () => {
      const [propRes, profileRes, agencyRes] = await Promise.all([
        supabase.from('properties').select('id', { count: 'exact', head: true }).eq('approval_status', 'approved').eq('status', 'active'),
        supabase.from('profiles').select('id', { count: 'exact', head: true }),
        supabase.from('agencies').select('id', { count: 'exact', head: true }).eq('is_verified', true),
      ]);
      return {
        properties: propRes.count || 0,
        users: profileRes.count || 0,
        agencies: agencyRes.count || 0,
      };
    },
  });

  const handleSearch = () => {
    if (activeTab === 'projects') {
      navigate(`/projects?q=${encodeURIComponent(searchQuery)}`);
      return;
    }
    const params = new URLSearchParams();
    if (searchQuery) params.set('q', searchQuery);
    if (selectedCity) params.set('city', selectedCity);
    if (heroSubtype) params.set('type', heroSubtype);
    if (minPrice) params.set('minPrice', minPrice);
    if (maxPrice) params.set('maxPrice', maxPrice);
    if (minArea) params.set('minArea', minArea);
    if (maxArea) params.set('maxArea', maxArea);
    if (heroCategory === 'homes' && beds !== 'all') params.set('beds', beds);

    let basePath: string;
    if (heroCategory === 'plots') basePath = '/plots';
    else if (heroCategory === 'commercial') basePath = '/commercial';
    else basePath = `/${activeTab}`;
    navigate(`${basePath}?${params.toString()}`);
  };

  const scroll = (ref: React.RefObject<HTMLDivElement>, direction: 'left' | 'right') => {
    if (ref.current) {
      ref.current.scrollBy({ left: direction === 'left' ? -300 : 300, behavior: 'smooth' });
    }
  };

  const getName = (val: any) => (typeof val === 'object' ? val?.en || '' : val || '');

  const statItems = [
    { icon: Building2, value: stats ? `${stats.properties.toLocaleString()}+` : '...', label: 'Properties Listed' },
    { icon: Users, value: stats ? `${stats.users.toLocaleString()}+` : '...', label: 'Registered Users' },
    { icon: MapPin, value: citiesData ? `${citiesData.length}+` : '...', label: 'Cities Covered' },
    { icon: TrendingUp, value: stats ? `${stats.agencies}+` : '...', label: 'Verified Agencies' },
  ];

  // Fallback agencies if none verified yet (kept minimal until DB populated)
  const fallbackAgencies: any[] = [];

  const fallbackProjects: any[] = [];

  const displayAgencies = verifiedAgencies?.length ? verifiedAgencies : fallbackAgencies;
  const displayProjects = approvedProjects?.length ? approvedProjects : fallbackProjects;

  const homepageJsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "Ethio Real Estates",
      url: "https://ethio-real-estate-hub.lovable.app",
      logo: "https://storage.googleapis.com/gpt-engineer-file-uploads/attachments/og-images/7f169d66-3955-46a0-86f4-b65736a568fd",
      description: "Ethiopia's leading real estate platform for buying, renting, and investing in property.",
    },
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      url: "https://ethio-real-estate-hub.lovable.app",
      name: "Ethio Real Estates",
      potentialAction: {
        "@type": "SearchAction",
        target: "https://ethio-real-estate-hub.lovable.app/buy?q={search_term_string}",
        "query-input": "required name=search_term_string",
      },
    },
  ];

  return (
    <Layout>
      <SEOHead
        title="Ethio Real Estates — Buy, Rent & Invest in Ethiopian Property"
        description="Search houses, apartments, villas, land and new projects for sale or rent in Addis Ababa, Adama, Hawassa and across Ethiopia."
        canonical="/"
        jsonLd={homepageJsonLd}
      />
      {/* Hero Section */}
      <section className="relative">
        <div className="relative">
          <img
            src={heroBg}
            alt="Addis Ababa cityscape"
            className="absolute inset-0 w-full h-full object-cover"
            width={1920}
            height={800}
            loading="eager"
            decoding="async"
            // @ts-expect-error - fetchpriority is a valid HTML attribute, React 18 types lag behind
            fetchpriority="high"
          />
          <div className="absolute inset-x-0 bottom-0 h-[15%] bg-gradient-to-t from-white via-white/80 to-transparent" />
          <div className="relative z-10 container pt-10 md:pt-16 pb-28 md:pb-36">
            <div className="max-w-3xl">
              <h1 className="font-display text-3xl sm:text-5xl md:text-[56px] font-bold leading-[1.05] tracking-tight text-foreground">
                Find Your Perfect
                <br />
                <span className="text-primary">Property in Ethiopia</span>
              </h1>
              <p className="mt-4 text-sm sm:text-base text-muted-foreground max-w-md">
                Search thousands of verified properties<br className="hidden sm:block" />
                to buy, rent or invest with confidence.
              </p>
            </div>

            {/* Centered search block */}
            <div className="mt-8 max-w-4xl mx-auto">
              {/* Tabs */}
              <div className="flex justify-center gap-1.5">
                {([
                  { key: 'buy', label: 'BUY', icon: Home },
                  { key: 'rent', label: 'RENT', icon: Building2 },
                  { key: 'projects', label: 'PROJECTS', icon: Briefcase },
                ] as const).map(({ key, label, icon: Icon }) => (
                  <button
                    key={key}
                    onClick={() => setActiveTab(key as any)}
                    className={`flex items-center gap-2 px-6 sm:px-10 py-2.5 rounded-t-md text-xs sm:text-sm font-bold tracking-wider transition-all ${
                      activeTab === key
                        ? 'bg-primary text-primary-foreground shadow-md'
                        : 'bg-slate-200/80 text-slate-700 hover:bg-slate-300/80'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {label}
                  </button>
                ))}
              </div>


              {/* Search Card */}
              <div className="bg-white rounded-md shadow-2xl p-4 md:p-5 border border-border/40">
                <div className="flex flex-col md:flex-row gap-2">
                  {activeTab !== 'projects' && (
                    <div className="md:w-[220px]">
                      <Select value={selectedCity} onValueChange={setSelectedCity}>
                        <SelectTrigger aria-label="Select city" className="h-12 border-border/60 text-sm rounded-md">
                          <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-primary shrink-0" />
                            <SelectValue />
                          </div>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Addis Ababa">All Addis Ababa</SelectItem>
                          {cityNames.filter(c => c !== 'Addis Ababa').map(city => (
                            <SelectItem key={city} value={city}>{city}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder={activeTab === 'projects' ? 'Search by project name or developer...' : 'Search by area, neighborhood, or keyword...'}
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleSearch()}
                      className="pl-10 h-12 border-border/60 text-sm rounded-md"
                    />
                  </div>
                  <Button onClick={handleSearch} className="h-12 px-10 font-bold text-sm tracking-wider rounded-md">
                    SEARCH
                  </Button>
                </div>

                {activeTab !== 'projects' && (
                  <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 mt-3">
                    <div>
                      <label className="text-[10px] font-medium text-muted-foreground mb-1 block pl-1">Property Type</label>
                      <HeroPropertyTypePopover
                        category={heroCategory}
                        setCategory={setHeroCategory}
                        subtype={heroSubtype}
                        setSubtype={setHeroSubtype}
                      />
                    </div>
                    <div>
                      <label className="text-[10px] font-medium text-muted-foreground mb-1 block pl-1">Min Price ({displayCurrency === 'ETB' ? 'Birr' : displayCurrency})</label>
                      <Select value={minPrice || 'any'} onValueChange={(v) => setMinPrice(v === 'any' ? '' : v)}>
                        <SelectTrigger className="h-10 border-border/60 text-xs"><SelectValue placeholder="No Min" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="any">No Min</SelectItem>
                          {['500000','1000000','2500000','5000000','10000000'].map(v => <SelectItem key={v} value={v}>{Number(v).toLocaleString()}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-[10px] font-medium text-muted-foreground mb-1 block pl-1">Max Price ({displayCurrency === 'ETB' ? 'Birr' : displayCurrency})</label>
                      <Select value={maxPrice || 'any'} onValueChange={(v) => setMaxPrice(v === 'any' ? '' : v)}>
                        <SelectTrigger className="h-10 border-border/60 text-xs"><SelectValue placeholder="No Max" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="any">No Max</SelectItem>
                          {['1000000','2500000','5000000','10000000','25000000','50000000'].map(v => <SelectItem key={v} value={v}>{Number(v).toLocaleString()}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-[10px] font-medium text-muted-foreground mb-1 block pl-1">Bedrooms</label>
                      <Select value={beds} onValueChange={setBeds}>
                        <SelectTrigger className="h-10 border-border/60 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Any</SelectItem>
                          {['1','2','3','4','5','6'].map(n => <SelectItem key={n} value={n}>{n === '6' ? '6+' : n}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-end">
                      <button
                        type="button"
                        onClick={handleSearch}
                        className="w-full h-10 px-3 rounded-md border border-border/60 text-xs font-semibold text-foreground hover:bg-muted flex items-center justify-center gap-2"
                      >
                        <Filter className="h-3.5 w-3.5" />
                        More Filters
                      </button>
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between mt-3 pt-3 border-t border-border/30">
                  <button
                    onClick={() => navigate(`/${activeTab === 'projects' ? 'projects' : activeTab}?advanced=1`)}
                    className="flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline"
                  >
                    <SlidersHorizontal className="h-3.5 w-3.5" />
                    Advanced Search
                  </button>
                  <button
                    className="flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline"
                    onClick={() => {
                      setSearchQuery('');
                      setSelectedCity(cityNames[0] || 'Addis Ababa');
                      setHeroSubtype('');
                      setMinPrice(''); setMaxPrice('');
                      setMinArea(''); setMaxArea('');
                      setBeds('all');
                    }}
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                    Reset
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>




        {/* Trust Strip — overlapping hero bottom */}
        <div className="container -mt-16 md:-mt-20 relative z-20">
          <div className="max-w-5xl mx-auto bg-white rounded-2xl shadow-xl border border-border/30 px-4 md:px-8 py-5 md:py-6 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-2 divide-y md:divide-y-0 md:divide-x divide-border/40">

            {[
              { icon: ShieldCheck, title: 'Verified Listings', desc: 'All properties are verified and trusted' },
              { icon: MapPin, title: 'Prime Locations', desc: 'Find properties in the best neighborhoods' },
              { icon: Handshake, title: 'Expert Support', desc: 'Get help from our local property experts' },
              { icon: Lock, title: 'Secure & Easy', desc: 'Safe transactions and hassle-free process' },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="flex items-start gap-3 px-2 md:px-4 pt-4 md:pt-0 first:pt-0">
                <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <Icon className="h-5 w-5 text-primary" strokeWidth={2.2} />
                </div>
                <div className="min-w-0">
                  <div className="font-bold text-sm text-foreground leading-tight">{title}</div>
                  <div className="text-[11px] md:text-xs text-muted-foreground leading-snug mt-0.5">{desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>


      {isMobile ? (
        <>
          {/* Mobile: streamlined Zameen-style layout in our brand colors */}
          <MobileBrowsePropertiesCard />
          <div className="px-3 pt-4">
            <BannerSlot placementCode="homepage_top_mobile" page="homepage" />
          </div>
          <FeedStoriesRow />

          <ViewedPropertiesRow />
          <MobilePostAdCTA />
          <NewPropertiesRow properties={(newProperties || []).slice(0, 12)} title="New Properties" />
          <div className="px-3 py-3">
            <BannerSlot placementCode="homepage_middle_mobile" page="homepage" />
          </div>
          <MobileTitaniumAgencies />
          <div className="px-3 py-4"><div className="border-highlight border-highlight-border rounded-2xl p-1.5 bg-highlight">
            <TrendingProjectsTwoRow projects={displayProjects.slice(0, 8)} />
          </div></div>
          <MobileNewsScroll />
        </>
      ) : (
        <>
          {/* Stories */}
          <FeedStoriesRow />

          {/* Browse Properties */}
          <section className="recommended-section bg-background"><div className="container py-6"><div className="border-highlight border-highlight-border rounded-2xl p-4 md:p-6 bg-highlight">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-xl md:text-2xl font-bold">Recommended for You</h2>
              <Button variant="link" className="text-primary hover:text-primary active:text-primary hover:bg-transparent active:bg-transparent text-sm font-semibold p-0" onClick={() => navigate('/buy')}>
                View All Properties <ArrowRight className="h-3.5 w-3.5 ml-1" />
              </Button>
            </div>
            {newProperties && newProperties.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {newProperties.slice(0, 10).map((property: any) => (
                  <RecommendedPropertyCard key={property.id} property={property} />
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground text-sm">No new properties yet.</div>
            )}
          </div></div></section>

          {/* Ad Ops — long banner above Explore More */}
          <section className="container py-0 flex justify-center">

            <BannerSlot placementCode="homepage_above_explore" page="homepage" className="w-full flex justify-center [&>div>div]:mx-auto [&_a]:mx-auto [&_img]:mx-auto" />
          </section>


          {/* Explore More */}
          <section className="container py-8">
            <h2 className="font-display text-lg md:text-xl font-bold mb-6">Explore More</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
              {(exploreTiles || []).map(tile => {
                const Icon = ICON_MAP[tile.icon_name] || Landmark;
                const customIcon: Record<string, { src: string; alt: string }> = {
                  HomeAdvisorAI: { src: homeAdvisorAiIcon, alt: 'HomeAdvisor AI' },
                  AVM: { src: avmIcon, alt: 'Automated Valuation Models' },
                  NewProjects: { src: newProjectsIcon, alt: 'New Projects' },
                  SocietyMaps: { src: societyMapsIcon, alt: 'Society Maps' },
                  AreaGuides: { src: areaGuidesIcon, alt: 'Area Guides' },
                  CostCalculator: { src: costCalculatorIcon, alt: 'Cost Calculator' },
                  EthioPropertyInvest: { src: ethioPropertyInvestIcon, alt: 'Ethio Property Invest' },
                };
                const custom = customIcon[tile.icon_name];
                return (
                  <button key={tile.id} onClick={() => navigate(tile.link_url)}
                    className="group flex items-start gap-2.5 sm:gap-3.5 p-3 sm:p-4 rounded-xl hover:shadow-md transition-all text-left">
                    <div className={`h-10 w-10 sm:h-12 sm:w-12 rounded-xl ${tile.background_class || 'bg-amber-50'} flex items-center justify-center shrink-0 overflow-hidden`}>
                      {custom ? (
                        <img src={custom.src} alt={custom.alt} className="h-full w-full object-contain" />
                      ) : (
                        <Icon className={`h-5 w-5 sm:h-6 sm:w-6 ${tile.color_class || 'text-amber-600'}`} />
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="font-bold text-xs sm:text-sm leading-tight">{localize(tile.title)}</div>
                      <div className="text-[10px] sm:text-xs text-muted-foreground mt-0.5 sm:mt-1 leading-snug line-clamp-2">{localize(tile.description)}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </section>

          <FavoritePropertiesRow />
          <ViewedPropertiesRow />
          <ViewedProjectsRow />
          <TitaniumAgencies />

          {/* Ad Ops — long banner between Agencies and Trending Projects */}
          <section className="container py-0 flex justify-center">
            <BannerSlot placementCode="homepage_above_agencies" page="homepage" className="w-full flex justify-center [&>div>div]:mx-auto [&_a]:mx-auto [&_img]:mx-auto" />
          </section>


          <section className="bg-background"><div className="container py-12"><div className="border-highlight border-highlight-border rounded-2xl p-1.5 md:p-2 bg-highlight">
            <TrendingProjectsTwoRow projects={displayProjects} />
          </div></div></section>
          <CommunitySection />
          
        </>
      )}

    </Layout>
  );
}
